import { Session } from "@supabase/supabase-js";
import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store/react";

import { AuthApi } from "@/data/api/authApi";
import { UserType } from "@/domain/auth";
import { setSentryUser } from "@/services/sentry";

type AuthStoreContextType = {
  session: Session | null;
  isAuthenticated?: boolean;
};

const authStore = createStore({
  context: {
    session: null,
    isAuthenticated: undefined, // Set to undefined initially, so we can render a loading state during initial auth check
  } as AuthStoreContextType,
  on: {
    // eslint-disable-next-line max-params
    authenticated: (_context, event: { session: Session }, enqueue) => {
      // Enqueue side effect to set the Sentry user
      enqueue.effect(() => {
        if (event.session) {
          void setSentryUser(event.session.user);
        }
      });

      // Update the context with the new session and isAuthenticated as true
      return {
        session: event.session,
        isAuthenticated: true,
      };
    },
    // eslint-disable-next-line max-params
    unauthenticated: (_context, _event, enqueue) => {
      // Enqueue side effect to reset the Sentry user
      enqueue.effect(() => {
        void setSentryUser(null);
      });

      // Update the context with null session and isAuthenticated as false
      return {
        session: null,
        isAuthenticated: false,
      };
    },
  },
});

// Selectors
export const useIsAuthenticated = () =>
  useSelector(authStore, state => state.context.isAuthenticated);
export const useCurrentUserId = () =>
  useSelector(authStore, state => state.context.session?.user.id ?? null);
export const useCurrentAuthUser = () =>
  useSelector(authStore, state => state.context.session?.user ?? null);
export const useCurrentUserType = () =>
  useSelector(
    authStore,
    state => state.context.session?.user?.user_metadata?.userType as UserType,
  );

// Listeners
export const setupAuthListener = () => {
  return AuthApi.subscribeToAuthStateChange(session => {
    if (session) {
      // If there is a session, dispatch the authenticated event
      authStore.send({ type: "authenticated", session });
    } else {
      // If there is no session, dispatch the unauthenticated event
      authStore.send({
        type: "unauthenticated",
      });
    }
  });
};
